import { useEffect, useState } from "react";
import "@/App.css";
import { motion, useScroll, useTransform } from "framer-motion";
import { 
  Bot, 
  TrendingUp, 
  Zap, 
  Shield, 
  ArrowRight, 
  ChevronRight,
  BarChart3,
  Clock,
  Target,
  Sparkles,
  Menu,
  X
} from "lucide-react";

// Animation variants
const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] }
};

const staggerContainer = {
  animate: {
    transition: { staggerChildren: 0.1 }
  }
};

// Header Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled 
          ? "bg-[#050505]/90 backdrop-blur-xl border-b border-white/10" 
          : "bg-transparent"
      }`}
      data-testid="header"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <a href="#" className="flex items-center gap-2" data-testid="logo">
            <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-lg text-white">PolyBot</span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-zinc-400 hover:text-white transition-colors text-sm" data-testid="nav-features">Features</a>
            <a href="#how-it-works" className="text-zinc-400 hover:text-white transition-colors text-sm" data-testid="nav-how-it-works">How it Works</a>
            <a href="#pricing" className="text-zinc-400 hover:text-white transition-colors text-sm" data-testid="nav-pricing">Pricing</a>
          </nav>

          <div className="hidden md:flex items-center gap-4">
            <button 
              className="text-zinc-400 hover:text-white transition-colors text-sm"
              data-testid="login-btn"
            >
              Log in
            </button>
            <button 
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2.5 rounded-full transition-all duration-300 glow-blue text-sm flex items-center gap-2"
              data-testid="get-started-btn"
            >
              Get Started
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          exit={{ opacity: 0, height: 0 }}
          className="md:hidden bg-[#0A0A0A] border-t border-white/10"
        >
          <div className="px-6 py-4 space-y-4">
            <a href="#features" className="block text-zinc-400 hover:text-white transition-colors">Features</a>
            <a href="#how-it-works" className="block text-zinc-400 hover:text-white transition-colors">How it Works</a>
            <a href="#pricing" className="block text-zinc-400 hover:text-white transition-colors">Pricing</a>
            <button className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-3 rounded-full transition-all duration-300">
              Get Started
            </button>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
};

// Hero Section
const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);

  return (
    <section className="relative min-h-screen flex items-center hero-bg pt-20" data-testid="hero-section">
      {/* Background Image */}
      <motion.div 
        style={{ y }}
        className="absolute inset-0 z-0"
      >
        <img 
          src="https://images.unsplash.com/photo-1762280040702-dbe2f4869712?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxtaW5pbWFsaXN0JTIwZGFyayUyMGdyYWRpZW50JTIwbWVzaHxlbnwwfHx8fDE3NzU3NTgwMzl8MA&ixlib=rb-4.1.0&q=85"
          alt="Background"
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/70 via-[#050505]/80 to-[#050505]" />
      </motion.div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
          >
            <motion.div 
              variants={fadeUp}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6"
            >
              <Sparkles className="w-4 h-4 text-blue-500" />
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-500">AI-Powered Trading</span>
            </motion.div>

            <motion.h1 
              variants={fadeUp}
              className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-none text-white mb-6"
            >
              Trade Smarter on{" "}
              <span className="gradient-text">Polymarket</span>
            </motion.h1>

            <motion.p 
              variants={fadeUp}
              className="text-base sm:text-lg font-normal leading-relaxed text-zinc-400 mb-8 max-w-xl"
            >
              Automate your prediction market strategies with advanced AI. 
              Real-time analysis, instant execution, and superior returns.
            </motion.p>

            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4">
              <button 
                className="btn-primary bg-blue-600 hover:bg-blue-700 text-white font-medium px-8 py-4 rounded-full transition-all duration-300 glow-blue flex items-center justify-center gap-2"
                data-testid="hero-cta-primary"
              >
                Start Trading Now
                <ArrowRight className="w-5 h-5" />
              </button>
              <button 
                className="bg-transparent hover:bg-white/10 text-white border border-white/20 font-medium px-8 py-4 rounded-full transition-all duration-300 flex items-center justify-center gap-2"
                data-testid="hero-cta-secondary"
              >
                Watch Demo
                <ChevronRight className="w-5 h-5" />
              </button>
            </motion.div>

            {/* Stats */}
            <motion.div variants={fadeUp} className="grid grid-cols-3 gap-8 mt-12 pt-8 border-t border-white/10">
              <div>
                <p className="stat-value text-3xl sm:text-4xl font-bold">$2.4M+</p>
                <p className="text-zinc-500 text-sm mt-1">Volume Traded</p>
              </div>
              <div>
                <p className="stat-value text-3xl sm:text-4xl font-bold">89%</p>
                <p className="text-zinc-500 text-sm mt-1">Win Rate</p>
              </div>
              <div>
                <p className="stat-value text-3xl sm:text-4xl font-bold">1.2K+</p>
                <p className="text-zinc-500 text-sm mt-1">Active Users</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Right - Notification Card */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="hidden lg:block"
          >
            <div className="relative">
              {/* Main Card */}
              <div className="glass-panel p-6 animate-float">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-white font-medium">Trade Executed</p>
                    <p className="text-zinc-500 text-sm">Just now</p>
                  </div>
                </div>
                <div className="bg-[#0A0A0A] rounded-xl p-4 mb-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-zinc-400 text-sm">Market</span>
                    <span className="text-white font-medium text-sm">Trump 2024 Win</span>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-zinc-400 text-sm">Position</span>
                    <span className="text-green-500 font-mono text-sm">YES @ $0.52</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-zinc-400 text-sm">Amount</span>
                    <span className="text-white font-mono text-sm">$500.00</span>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-zinc-500 text-sm">Potential Return</span>
                  <span className="text-green-500 font-bold">+$461.54</span>
                </div>
              </div>

              {/* Secondary Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="absolute -bottom-4 -left-4 glass-panel p-4 w-64"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
                    <BarChart3 className="w-4 h-4 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-white text-sm font-medium">AI Analysis</p>
                    <p className="text-green-500 text-xs font-mono">85% confidence</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Features Section (Bento Grid)
const Features = () => {
  const features = [
    {
      icon: Bot,
      title: "AI-Powered Analysis",
      description: "Advanced machine learning algorithms analyze market trends and sentiment in real-time.",
      size: "large",
      image: "https://images.unsplash.com/photo-1760931969401-9bd6ee902798?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxyb2JvdCUyMGF1dG9tYXRpb24lMjB0ZWNoJTIwYWJzdHJhY3R8ZW58MHx8fHwxNzc1NzU4MDI1fDA&ixlib=rb-4.1.0&q=85"
    },
    {
      icon: Zap,
      title: "Instant Execution",
      description: "Lightning-fast order execution with sub-second latency.",
      size: "small"
    },
    {
      icon: Shield,
      title: "Secure & Private",
      description: "Bank-grade encryption and non-custodial architecture.",
      size: "small"
    },
    {
      icon: Target,
      title: "Smart Strategies",
      description: "Pre-built and custom trading strategies to maximize returns.",
      size: "medium"
    },
    {
      icon: Clock,
      title: "24/7 Monitoring",
      description: "Automated monitoring and alerts for all your positions.",
      size: "medium"
    }
  ];

  return (
    <section id="features" className="py-24 sm:py-32 relative" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-500 mb-4 block">Features</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white mb-4">
            Everything You Need to Win
          </h2>
          <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto">
            Professional-grade tools designed for serious prediction market traders.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 lg:gap-8">
          {/* Large Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="md:col-span-8 bento-card glass-panel p-8 relative overflow-hidden min-h-[400px]"
            data-testid="feature-card-main"
          >
            <div className="relative z-10">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mb-4">
                <Bot className="w-6 h-6 text-blue-500" />
              </div>
              <h3 className="text-xl sm:text-2xl font-medium tracking-tight text-white/90 mb-2">
                {features[0].title}
              </h3>
              <p className="text-zinc-400 max-w-md">
                {features[0].description}
              </p>
            </div>
            <img 
              src={features[0].image}
              alt="AI Robot"
              className="absolute right-0 bottom-0 w-64 h-64 object-cover opacity-50 rounded-tl-3xl"
            />
          </motion.div>

          {/* Small Cards */}
          {features.slice(1, 3).map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="md:col-span-4 bento-card glass-panel p-8"
              data-testid={`feature-card-${index + 1}`}
            >
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mb-4">
                <feature.icon className="w-6 h-6 text-blue-500" />
              </div>
              <h3 className="text-xl font-medium tracking-tight text-white/90 mb-2">
                {feature.title}
              </h3>
              <p className="text-zinc-400 text-sm">
                {feature.description}
              </p>
            </motion.div>
          ))}

          {/* Medium Cards */}
          {features.slice(3).map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: (index + 2) * 0.1 }}
              className="md:col-span-6 bento-card glass-panel p-8"
              data-testid={`feature-card-${index + 3}`}
            >
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mb-4">
                <feature.icon className="w-6 h-6 text-blue-500" />
              </div>
              <h3 className="text-xl font-medium tracking-tight text-white/90 mb-2">
                {feature.title}
              </h3>
              <p className="text-zinc-400 text-sm">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorks = () => {
  const steps = [
    {
      number: "01",
      title: "Connect Your Wallet",
      description: "Link your wallet securely using our non-custodial connection. Your funds stay in your control."
    },
    {
      number: "02",
      title: "Set Your Strategy",
      description: "Choose from pre-built strategies or customize your own based on risk tolerance and goals."
    },
    {
      number: "03",
      title: "Let AI Trade",
      description: "Our algorithms monitor markets 24/7, executing optimal trades based on real-time analysis."
    },
    {
      number: "04",
      title: "Watch Profits Grow",
      description: "Track your portfolio performance in real-time with detailed analytics and insights."
    }
  ];

  return (
    <section id="how-it-works" className="py-24 sm:py-32 relative grid-pattern" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Steps */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-12"
            >
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-500 mb-4 block">How It Works</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
                Get Started in Minutes
              </h2>
            </motion.div>

            <div className="space-y-8">
              {steps.map((step, index) => (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="timeline-step flex gap-6"
                  data-testid={`step-${step.number}`}
                >
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 rounded-full bg-blue-500/20 border border-blue-500/50 flex items-center justify-center">
                      <span className="font-mono text-blue-500 text-sm font-bold">{step.number}</span>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl font-medium text-white mb-2">{step.title}</h3>
                    <p className="text-zinc-400">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Right - Image */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative hidden lg:block"
          >
            <div className="glass-panel p-2 rounded-2xl overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1762279389083-abf71f22d338?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGNoYXJ0JTIwbmVvbiUyMGRhdGElMjBsaW5lfGVufDB8fHx8MTc3NTc1ODAyNXww&ixlib=rb-4.1.0&q=85"
                alt="Data Visualization"
                className="w-full h-auto rounded-xl opacity-80"
              />
            </div>
            
            {/* Floating stat */}
            <motion.div
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -bottom-6 -left-6 glass-panel p-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-zinc-400 text-xs">Monthly ROI</p>
                  <p className="text-white font-bold text-lg">+34.7%</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTA = () => {
  return (
    <section id="pricing" className="py-24 sm:py-32" data-testid="cta-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="cta-section rounded-3xl p-8 sm:p-12 lg:p-16 text-center"
        >
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-blue-500 mb-4 block">Limited Time</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white mb-4">
            Ready to Start Winning?
          </h2>
          <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto mb-8">
            Join 1,200+ traders already using PolyBot to dominate prediction markets. 
            First 100 users get lifetime premium access.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              className="btn-primary bg-blue-600 hover:bg-blue-700 text-white font-medium px-10 py-4 rounded-full transition-all duration-300 glow-blue animate-pulse-glow flex items-center justify-center gap-2"
              data-testid="cta-start-btn"
            >
              <Zap className="w-5 h-5" />
              Start Trading Now
            </button>
            <button 
              className="bg-transparent hover:bg-white/10 text-white border border-white/20 font-medium px-10 py-4 rounded-full transition-all duration-300 flex items-center justify-center gap-2"
              data-testid="cta-demo-btn"
            >
              Schedule Demo
            </button>
          </div>

          <p className="text-zinc-500 text-sm mt-6">
            No credit card required • Free trial • Cancel anytime
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-[#050505] py-20 border-t border-white/10 overflow-hidden" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-lg text-white">PolyBot</span>
            </div>
            <p className="text-zinc-500 text-sm">
              AI-powered trading for prediction markets.
            </p>
          </div>

          <div>
            <h4 className="text-white font-medium mb-4">Product</h4>
            <ul className="space-y-2">
              <li><a href="#features" className="text-zinc-500 hover:text-white transition-colors text-sm">Features</a></li>
              <li><a href="#how-it-works" className="text-zinc-500 hover:text-white transition-colors text-sm">How it Works</a></li>
              <li><a href="#pricing" className="text-zinc-500 hover:text-white transition-colors text-sm">Pricing</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-medium mb-4">Company</h4>
            <ul className="space-y-2">
              <li><a href="#" className="text-zinc-500 hover:text-white transition-colors text-sm">About</a></li>
              <li><a href="#" className="text-zinc-500 hover:text-white transition-colors text-sm">Blog</a></li>
              <li><a href="#" className="text-zinc-500 hover:text-white transition-colors text-sm">Careers</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-medium mb-4">Legal</h4>
            <ul className="space-y-2">
              <li><a href="#" className="text-zinc-500 hover:text-white transition-colors text-sm">Privacy</a></li>
              <li><a href="#" className="text-zinc-500 hover:text-white transition-colors text-sm">Terms</a></li>
              <li><a href="#" className="text-zinc-500 hover:text-white transition-colors text-sm">Security</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-zinc-500 text-sm">
            © 2025 PolyBot. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a href="#" className="text-zinc-500 hover:text-white transition-colors text-sm">Twitter</a>
            <a href="#" className="text-zinc-500 hover:text-white transition-colors text-sm">Discord</a>
            <a href="#" className="text-zinc-500 hover:text-white transition-colors text-sm">Telegram</a>
          </div>
        </div>
      </div>

      {/* Massive Footer Text */}
      <div className="mt-16 overflow-hidden">
        <p className="footer-massive-text text-center">
          POLYMARKET BOT
        </p>
      </div>
    </footer>
  );
};

// Main App
function App() {
  return (
    <div className="App bg-[#050505] min-h-screen">
      <Header />
      <main>
        <Hero />
        <Features />
        <HowItWorks />
        <CTA />
      </main>
      <Footer />
    </div>
  );
}

export default App;
